type LogLevel = 'log' | 'error' | 'warn' | 'info' | 'debug';

interface LogPayload {
  level: LogLevel;
  message: string;
  data?: any;
  url: string;
  timestamp: string;
  userAgent?: string;
}

const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.DEV;

/**
 * Send log to backend (optional - configure your endpoint)
 */
const sendToBackend = async (payload: LogPayload): Promise<void> => {
  // Only send errors and warnings to backend in production
  if (isDevelopment || (payload.level !== 'error' && payload.level !== 'warn')) {
    return;
  }

  try {
    // Uncomment and configure your logging endpoint
    // await fetch('/api/logs', {
    //   method: 'POST',
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify(payload)
    // });
  } catch (error) {
    // Silently fail - don't let logging break the app
  }
};

/**
 * Core logging function
 */
const logMessage = (level: LogLevel, ...args: any[]): void => {
  // In development, always show console messages
  if (isDevelopment) {
    const consoleMethod = console[level] || console.log;
    consoleMethod(...args);
  }

  // Prepare payload for backend
  const message = args
    .map(arg => {
      if (typeof arg === 'object') {
        try {
          return JSON.stringify(arg);
        } catch {
          return String(arg);
        }
      }
      return String(arg);
    })
    .join(' ');

  const payload: LogPayload = {
    level,
    message,
    data: args.length === 1 && typeof args[0] === 'object' ? args[0] : args,
    url: typeof window !== 'undefined' ? window.location.href : '',
    timestamp: new Date().toISOString(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
  };

  // Send to backend (async, non-blocking)
  sendToBackend(payload);
};

/**
 * Logger object with all console methods
 */
export const logger = {
  log: (...args: any[]) => logMessage('log', ...args),
  error: (...args: any[]) => logMessage('error', ...args),
  warn: (...args: any[]) => logMessage('warn', ...args),
  info: (...args: any[]) => logMessage('info', ...args),
  debug: (...args: any[]) => logMessage('debug', ...args),
};

/**
 * For backwards compatibility - log error with additional context
 */
export const logError = (error: Error | string, context?: Record<string, any>): void => {
  if (error instanceof Error) {
    logger.error(error.message, { stack: error.stack, ...context });
  } else {
    logger.error(error, context);
  }
};

export default logger;
